// The running-goal SAVE rules — ONE sequence for the Profile screen and Onboarding (owner 2026-11-02: one validation
// mechanism, no parallel business logic). The backend stays the authority: whatever passes here is checked again by
// PUT /profile on the WHOLE training set, and since 2026-11-02 PUT /profile refuses — with or without an override —
// any running profile the plan engine cannot build.
//
//   1. required running fields                      -> block
//   2. km x days matrix / the 10K level envelope     -> block (precise reason) or the LoadBalanceModal (fix only)
//   3. the plan engine's own answer                  -> block with its Hebrew reason, never "שמור בכל זאת"
//      (GET /policy/running-availability: goal x weekly km x days x level — the marathon policy, the weekly-km floor
//      by goal AND level, the 5K / 10K product envelopes; no threshold is copied here)
//   4. the goal-time vs volume guardrail             -> GoalGuardrailModal ("continue anyway" unless hardBlock)
//   5. save WITHOUT an override; a server rejection  -> block "השמירה נכשלה" with the server's own message
// There is no overridable weekly-volume warning any more (owner 2026-11-02, P0): the engine never read the override,
// so "שמור בכל זאת" stored profiles whose plan then failed. A confirmed goal-time warning skips nothing — it comes last.
// d = { goal_distance, goal_time, avg_weekly_km, preferred_days, level } (the form's raw values).
import { validateKmDaysMatrix, validateUserGoal } from './goalValidator';

/** 1. The required running fields that are missing (Hebrew labels, in Profile's order). */
export function runningMissingFields(d) {
  const missing = [];
  if (!d.goal_distance) missing.push('מרחק יעד');
  if (!d.goal_time) missing.push('זמן יעד');
  if (!d.avg_weekly_km) missing.push('ק"מ שבועי ממוצע');
  return missing;
}
export function runningMissingMessage(missing) {
  return `שדות חובה חסרים בפרופיל ריצה:\n• ${missing.join('\n• ')}\n\nבלי שדות אלו אי-אפשר לבנות תכנית אימונים מותאמת.`;
}

export function runningDaysCount(preferredDays) {
  return preferredDays ? String(preferredDays).split(' ').filter((x) => x).length : 0;
}

/** 2. The km x days matrix (10K: the runner-level band + envelope) for THIS distance and level. */
export function runningMatrixCheck(d) {
  return validateKmDaysMatrix(d.avg_weekly_km, runningDaysCount(d.preferred_days),
                              { distance: d.goal_distance, level: d.level });
}

/** 3. The plan engine's answer for this goal / volume / days / level: the server's JSON, or null when it cannot be
 *  read (the save then meets the same rule on the server). Never throws. */
export const PLAN_CHECK_TIMEOUT_MS = 8000;
export async function fetchRunningAvailability(fetcher, apiBase, d, { timeoutMs = PLAN_CHECK_TIMEOUT_MS } = {}) {
  const q = new URLSearchParams();
  if (d.goal_distance) q.set('goal_distance', String(d.goal_distance));
  const km = parseFloat(d.avg_weekly_km);
  if (Number.isFinite(km) && km > 0) q.set('avg_weekly_km', String(km));
  const days = runningDaysCount(d.preferred_days);
  if (days) q.set('training_days_per_week', String(days));
  if (d.level) q.set('level', String(d.level));
  // the chosen days themselves and the long-run day: the engine checks where each session can fall (owner 2026-11-02)
  const chosen = String(d.preferred_days || '').split(/\s+/).filter(Boolean).join(' ');
  if (chosen) q.set('preferred_days', chosen);
  if (d.preferred_long_run_day) q.set('preferred_long_run_day', String(d.preferred_long_run_day));
  if (d.goal_time) q.set('goal_time', String(d.goal_time));
  let timer = null;
  try {
    const res = await Promise.race([
      fetcher(`${apiBase}/policy/running-availability?${q}`),
      new Promise((resolve) => { timer = setTimeout(() => resolve(null), timeoutMs); }),
    ]);
    if (!res || !res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
/** Does the form CHANGE a field the running plan is built from, against what is stored (`saved`; null = a new
 *  profile)? The engine check runs only then — as on the server — so a stored profile that predates the rule can still
 *  save its injury state or max HR, and no profile becomes unbuildable without changing one of these. */
const PLAN_FIELDS = ['goal_distance', 'avg_weekly_km', 'preferred_days', 'level', 'preferred_long_run_day'];
const _norm = (f, v) => (f === 'avg_weekly_km'
  ? (Number.isFinite(parseFloat(v)) ? parseFloat(v) : null)
  : String(v ?? '').split(/\s+/).filter(Boolean).join(' ').toLowerCase());
export function runningPlanChanged(saved, current) {
  if (!saved) return true;
  return PLAN_FIELDS.some((f) => _norm(f, saved[f]) !== _norm(f, current[f]));
}
export const PLAN_UNAVAILABLE_MSG = 'מנוע התוכניות לא יכול לבנות תכנית לנתונים האלה. עדכן את הנפח, ימי הריצה, הרמה או מרחק היעד.';
/** { block: <Hebrew reason> } when the engine cannot build a plan for this profile, else { block: null }. */
export async function runningPlanCheck(d, { fetcher, apiBase }) {
  const av = await fetchRunningAvailability(fetcher, apiBase, d);
  if (av && av.supported === false) return { block: av.userMessage || PLAN_UNAVAILABLE_MSG };
  return { block: null };
}

/** The allowed weekly-km line for the athlete's choices, from the server's answer (allowedKmRange — the MASTER
 *  envelope for the day count, the level floor, the km x days matrix): the same words on both screens. null when the
 *  server said nothing (not asked yet / unreachable). */
export function allowedRangeText(av, days) {
  if (!av || !days) return null;
  const r = av.allowedKmRange;
  if (r) return `טווח נתמך לתוכנית: ${r.min}–${r.max} ק״מ בשבוע (${days} ימי ריצה)`;
  if (r === null) return `אין נפח שבועי נתמך עם ${days} ימי ריצה למרחק הזה — יש לשנות את מספר ימי הריצה.`;
  return null;
}

/** 4. The goal-time vs volume guardrail. */
export function runningGoalGuardrail(d) {
  return validateUserGoal(parseFloat(d.avg_weekly_km), d.goal_distance, d.goal_time);
}

/** 5. The server's message for a rejected PUT /profile: detail.message / a string detail / the first 422 entry (no
 *  "Value error," prefix, no developer override hint) / message — else the fallback. */
export function saveErrorMessage(body, fallback = 'שגיאה בשמירת הפרופיל') {
  if (body?.detail?.message) return body.detail.message;
  if (typeof body?.detail === 'string') return body.detail;
  if (Array.isArray(body?.detail) && body.detail.length) {
    return String(body.detail[0]?.msg || fallback)
      .replace(/^Value error,\s*/i, '')
      .replace(/\s*לעקיפה:.*$/, '')
      .trim() || fallback;
  }
  if (body?.message) return body.message;
  return fallback;
}

/** The fields Onboarding saves together with the goal: the whole running training set, so PUT /profile checks the
 *  COMBINATION (distance, time, level, volume, days, long-run day) exactly as it does for the Profile screen. Only
 *  fields that hold a value are sent — nothing else in the profile is touched or cleared. */
export function trainingSetPayload(form) {
  const out = {};
  if (form.level) out.level = form.level;
  const km = parseFloat(form.avg_weekly_km);
  if (Number.isFinite(km) && km > 0) out.avg_weekly_km = km;
  const prefDays = String(form.preferred_days || '').trim();
  if (prefDays) {
    out.preferred_days = prefDays;
    out.training_days_per_week = runningDaysCount(prefDays);
  }
  if (form.preferred_long_run_day) out.preferred_long_run_day = form.preferred_long_run_day;
  return out;
}
